import { APIResponse, PaginatedAPIResponse } from "../models/NetworkResponse";
import { getAPI, postAPI, putAPI } from "./Networking";
import { ProjectRequest } from "../models/Project/Project";
import { ProjectRequestFilters } from "../models/Project/ProjectRequestFilters";

export default class ProjectAPI {
  getProjectRequestList = async (
    params: ProjectRequestFilters,
  ): Promise<PaginatedAPIResponse<ProjectRequest>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no)
        queryParams.append("page_no", params.page_no.toString());

      if (params.limit) queryParams.append("limit", params.limit.toString());

      if (params.sort_by) queryParams.append("sort_by", params.sort_by);

      if (params.sort_type)
        queryParams.append("sort_type", params.sort_type.toString());

      if (params.search_text && params.search_text !== "")
        queryParams.append("search_text", params.search_text);

      if (params.order_id)
        queryParams.append("order_id", params.order_id.toString());

      if (params.order_type)
        queryParams.append("order_type", params.order_type.toString());

      if (params.payment_status)
        queryParams.append("payment_status", params.payment_status.toString());

      if (params.finance_approval_status)
        queryParams.append(
          "finance_approval_status",
          params.finance_approval_status.toString(),
        );

      if (params.status) queryParams.append("status", params.status.toString());

      queryParams.append("active", params.active === false ? "false" : "true");

      const response = await getAPI(
        `/v5/project-requests?${queryParams.toString()}`,
      );
      return response.result as PaginatedAPIResponse<ProjectRequest>;
    } catch (error) {
      throw error;
    }
  };

  getRequestsByOrderId = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/project-requests/order-id/${orderId}`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  rejectRequest = async (
    id: number,
    cancelReason: string,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/project-requests/request-id/${id}`, {
        status: 3,
        cancel_reason: cancelReason,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  approveRequest = async (id: number): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/project-requests/request-id/${id}`, {
        status: 2,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  financeApproveRequest = async (requestId: number): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/project-requests/request-id/${requestId}/finance-approve`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  financeRejectRequest = async (
    requestId: number,
    rejectionReason: string,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/project-requests/request-id/${requestId}/finance-reject`,
        { rejection_reason: rejectionReason },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getTypeOfOrder = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/admin/projects/orderTypes`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getTypeOfShipments = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/admin/projects/shippingTypes`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getModeOfShipments = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/admin/projects/shipmentModes`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPaymentStatuses = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/admin/projects/paymentStatuses`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPaymentModes = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/admin/projects/paymentModes`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  raiseProjectRequest = async (
    data: Partial<ProjectRequest>,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/project-requests`, data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  createDefaultTasks = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v5/projects/${orderId}/create-default-tasks`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
}
